import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import { clearSessionArtifacts, clearSessionArtifactsGlobal } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { clearAllMfaSatisfied } from "@/lib/device-session";
import { clearAllTestSendAttempts } from "@/lib/test-send-attempt-store";
import { clearAllClassicBatchEntryDrafts } from "@/lib/classic-batch-entry-draft";
import { setSentryUser } from "@/monitoring/sentry";
import type { User, Session, AuthChangeEvent } from "@supabase/supabase-js";

type AuthError = {
  type: "session_fetch_error" | "token_refresh_error" | "network_error" | "session_fetch_aborted";
  message: string;
  originalError?: unknown;
};

type AuthSnapshot = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  authError: AuthError | null;
  lastAuthEvent: AuthChangeEvent | null;
  /** Non-secret, tab-local identity; advances synchronously on session/principal changes. */
  sessionGeneration: number;
};

const RETRYABLE_ABORT_LIMIT = 2;
export const AUTH_SESSION_TIMEOUT_MS = 10_000;

let snapshot: AuthSnapshot = {
  user: null,
  session: null,
  isLoading: true,
  authError: null,
  lastAuthEvent: null,
  sessionGeneration: 0,
};

let initialized = false;
let initialCheckComplete = false;
let bootstrapPromise: Promise<void> | null = null;
let sessionRehydrationPromise: Promise<boolean> | null = null;
let activeListenerCount = 0;
let authStateVersion = 0;

const listeners = new Set<() => void>();

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

class AuthSessionTimeoutError extends Error {
  constructor() {
    super(`Session restore timed out after ${AUTH_SESSION_TIMEOUT_MS}ms`);
    this.name = "AuthSessionTimeoutError";
  }
}

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => reject(new AuthSessionTimeoutError()), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

const isAbortError = (error: unknown): boolean => {
  if (!(error instanceof Error) && typeof error !== "object") {
    return false;
  }

  const candidate = error as { name?: string; message?: string };
  if (candidate?.name === "AbortError") return true;

  const errorMessage = candidate?.message?.toLowerCase();
  return !!errorMessage && errorMessage.includes("aborted");
};

const areUsersEquivalent = (a: User | null, b: User | null) => {
  if (a === null || b === null) return a === b;
  return a.id === b.id && a.email === b.email;
};

const areSessionsEquivalent = (a: Session | null, b: Session | null) => {
  if (a === null || b === null) return a === b;
  return a.access_token === b.access_token && a.refresh_token === b.refresh_token;
};

const areAuthErrorsEquivalent = (a: AuthError | null, b: AuthError | null) => {
  if (a === null || b === null) return a === b;
  if (a.type !== b.type || a.message !== b.message) return false;
  if (a.originalError === b.originalError) return true;
  const aError = a.originalError as { message?: string } | undefined;
  const bError = b.originalError as { message?: string } | undefined;
  return (aError?.message ?? null) === (bError?.message ?? null);
};

const emit = () => {
  listeners.forEach((listener) => listener());
};

/** A signed-in user giving way to someone else (`nextUserId`) or to no one (`null`). */
export interface SignedInUserChange {
  previousUserId: string;
  nextUserId: string | null;
  /**
   * Where an explicit sign-out asked to land (`signOut({ redirectTo })`, "/auth" by default).
   * Null when the session ended some other way: it expired, or was signed out in another tab.
   */
  signOutRedirect: string | null;
}

// Told when a signed-in user gives way to someone else or to no one, synchronously in the step
// that publishes the change, so a subscriber can drop what it holds for the previous user before
// anything renders the next one (#7022). src/lib/user-scoped-query-client.tsx gives the next user
// a fresh QueryClient, and src/lib/reload-on-user-change.ts reloads the page (#7026). They
// register rather than being imported here, so this module constructs neither.
const signedInUserChangeListeners = new Set<(change: SignedInUserChange) => void>();

// Every explicit sign-out currently running, oldest first, so the change one of them causes can
// carry its destination. Supabase's SIGNED_OUT event names no caller, so once a slower sign-out
// and the one that went ahead of it (see SIGN_OUT_QUEUE_WAIT_MS) are both still in flight, there is
// no way to tell which of them a given SIGNED_OUT belongs to — reporting either as if it were
// certain can send the user to the wrong destination (Codex on this promotion PR, #7037: a manual
// sign-out that outlasted the wait could show the OTHER call's "session expired" destination).
const pendingSignOuts: { redirectTo: string }[] = [];

// The one destination to use for a SIGNED_OUT right now, or null when there isn't a single answer:
// nothing in flight, or several in flight that don't all want the same place (Codex on #7037,
// round 2: a stalled sign-out retried with the same options is not actually ambiguous just because
// two entries exist — only a genuine disagreement is).
const currentSignOutRedirect = (): string | null => {
  if (pendingSignOuts.length === 0) return null;
  const [first, ...rest] = pendingSignOuts;
  return rest.every((entry) => entry.redirectTo === first.redirectTo) ? first.redirectTo : null;
};

export function onSignedInUserChange(listener: (change: SignedInUserChange) => void): () => void {
  signedInUserChangeListeners.add(listener);
  return () => {
    signedInUserChangeListeners.delete(listener);
  };
}

const patchSnapshot = (patch: Partial<AuthSnapshot>) => {
  const next: AuthSnapshot = {
    ...snapshot,
    ...patch,
  };

  if (
    areUsersEquivalent(snapshot.user, next.user) &&
    areSessionsEquivalent(snapshot.session, next.session) &&
    snapshot.isLoading === next.isLoading &&
    snapshot.lastAuthEvent === next.lastAuthEvent &&
    areAuthErrorsEquivalent(snapshot.authError, next.authError)
  ) {
    return;
  }

  next.sessionGeneration = snapshot.sessionGeneration + (
    !areSessionsEquivalent(snapshot.session, next.session) || snapshot.user?.id !== next.user?.id ? 1 : 0
  );
  snapshot = next;
  emit();
};

const syncAuthState = (nextSession: Session | null) => {
  const nextUser = nextSession?.user ?? null;
  const shouldUpdateSession = !areSessionsEquivalent(snapshot.session, nextSession);
  const shouldUpdateUser = !areUsersEquivalent(snapshot.user, nextUser);

  // Every session change lands here (bootstrap, the auth listener, explicit restores), so this is
  // where a change of user is detected (#7022). Not on a first sign-in (nothing user-specific is
  // held yet) or a same-user refresh.
  const previousUserId = snapshot.user?.id ?? null;
  const nextUserId = nextUser?.id ?? null;
  if (previousUserId !== null && nextUserId !== previousUserId) {
    const change: SignedInUserChange = {
      previousUserId,
      nextUserId,
      signOutRedirect: nextUserId === null ? currentSignOutRedirect() : null,
    };
    signedInUserChangeListeners.forEach((listener) => {
      try {
        listener(change);
      } catch (error) {
        logger.error("[useAuth] Signed-in user change listener failed:", {
          error: logger.sanitizeForUser(error instanceof Error ? error.message : "Unknown error"),
        });
      }
    });
  }

  if (shouldUpdateSession || shouldUpdateUser) {
    patchSnapshot({
      session: nextSession,
      user: nextUser,
      authError: nextSession ? null : snapshot.authError,
    });
  } else if (nextSession && snapshot.authError !== null) {
    patchSnapshot({ authError: null });
  }

  return {
    shouldUpdateSession,
    shouldUpdateUser,
  };
};

const setAuthErrorIfDifferent = (next: AuthError | null) => {
  if (areAuthErrorsEquivalent(snapshot.authError, next)) {
    return;
  }
  patchSnapshot({ authError: next });
};

const fetchSession = async (retryAttempt = 0): Promise<Session | null> => {
  const authStateVersionAtStart = authStateVersion;

  try {
    const {
      data: { session: freshSession },
      error,
    } = await withTimeout(supabase.auth.getSession(), AUTH_SESSION_TIMEOUT_MS);

    if (error) {
      throw error;
    }

    const { shouldUpdateSession, shouldUpdateUser } = syncAuthState(freshSession ?? null);
    if (shouldUpdateSession || shouldUpdateUser) {
      logger.debug("[useAuth] Session fetched successfully", {
        hasSession: !!freshSession,
      });
    }

    return freshSession ?? null;
  } catch (error) {
    if (error instanceof AuthSessionTimeoutError && authStateVersion !== authStateVersionAtStart) {
      // An authoritative auth event completed while getSession was pending.
      // Trust the event instead of turning a settled signed-out/session state
      // into a false session-fetch error when the stale read times out.
      return snapshot.session;
    }

    if (isAbortError(error) && retryAttempt < RETRYABLE_ABORT_LIMIT) {
      logger.debug("[useAuth] Session fetch aborted, retrying", {
        retryAttempt: retryAttempt + 1,
        maxRetries: RETRYABLE_ABORT_LIMIT,
      });
      await sleep(150 * (retryAttempt + 1));
      return fetchSession(retryAttempt + 1);
    }

    if (isAbortError(error)) {
      logger.debug("[useAuth] Session fetch aborted after retries; treating as transient");
      setAuthErrorIfDifferent({
        type: "session_fetch_aborted",
        message: logger.sanitizeForUser(error instanceof Error ? error.message : "Session fetch was aborted"),
        originalError: error,
      });
      return null;
    }

    logger.warn("[useAuth] Session fetch failed:", {
      error: logger.sanitizeForUser(error instanceof Error ? error.message : "Failed to fetch session"),
    });
    setAuthErrorIfDifferent({
      type: "session_fetch_error",
      message: logger.sanitizeForUser(error instanceof Error ? error.message : "Failed to fetch session"),
      originalError: error,
    });
    return null;
  }
};

/**
 * Initialize auth state from cookie-persisted session.
 *
 * With @supabase/ssr, the session is stored in first-party cookies and
 * automatically restored on page load — no need for the legacy edge-function
 * bootstrap against auth-refresh.
 */
const bootstrapAuth = async () => {
  try {
    await fetchSession();
    if (snapshot.session === null && snapshot.authError === null) {
      // No session is a valid unauthenticated state.
      setAuthErrorIfDifferent(null);
    }
  } catch (error) {
    logger.error("[useAuth] Failed to initialize auth session:", {
      error: logger.sanitizeForUser(error instanceof Error ? error.message : "Unable to initialize session"),
    });
    setAuthErrorIfDifferent({
      type: "network_error",
      message: logger.sanitizeForUser(error instanceof Error ? error.message : "Failed to fetch session"),
      originalError: error,
    });
  } finally {
    initialCheckComplete = true;
    patchSnapshot({ isLoading: false });
    if (activeListenerCount === 0) {
      bootstrapPromise = null;
    }
  }
};

const ensureInitialized = () => {
  if (initialized) {
    return;
  }

  initialized = true;

  supabase.auth.onAuthStateChange((event, newSession) => {
    // Supabase can emit USER_UPDATED without a session payload during profile/MFA updates.
    // Treat that as non-authoritative and retain the existing session to avoid false
    // unauthenticated redirects (e.g. bouncing from /mfa/setup back to /auth).
    const shouldIgnoreEmptyNonTerminalEvent =
      newSession === null &&
      event !== "SIGNED_OUT" &&
      event !== "INITIAL_SESSION";

    patchSnapshot({
      lastAuthEvent: snapshot.lastAuthEvent === event ? snapshot.lastAuthEvent : event,
    });
    if (!shouldIgnoreEmptyNonTerminalEvent) {
      authStateVersion += 1;
      syncAuthState(newSession ?? null);
    }

    // Keep Sentry's scope in sync with the auth state. Only the user
    // id is attached — email/name are scrubbed by `scrubEvent` even if
    // they leak through, but we don't supply them at all here. The
    // null branch fires on SIGNED_OUT so a subsequent error isn't
    // misattributed to the previous user on shared devices.
    if (!shouldIgnoreEmptyNonTerminalEvent) {
      setSentryUser(newSession?.user?.id ?? null);
    }

    logger.debug("[useAuth] Auth state changed:", {
      event,
      hasSession: !!newSession,
      ignoredEmptySession: shouldIgnoreEmptyNonTerminalEvent,
      userId: newSession?.user?.id ?? null,
    });

    if (event === "SIGNED_OUT") {
      logger.debug("[useAuth] SIGNED_OUT event received");
      // The one place every way a session ends passes through: the explicit logout methods below,
      // an edge function's session_revoked response (safeSignOut), and a sign-out in another tab.
      // Remove the stored test-send attempts here so none of them can outlive the account that made
      // them (Codex on #6606: clearing only in the explicit logout methods missed the others).
      clearAllTestSendAttempts();
      // An unsaved contribution batch holds donor names and amounts. It is kept per user, but the next person
      // to sign in on this tab should not be able to find any of them in session storage (#6069).
      clearAllClassicBatchEntryDrafts();
    }

    if (initialCheckComplete) {
      patchSnapshot({ isLoading: false });
    }
  });

  if (!bootstrapPromise) {
    bootstrapPromise = bootstrapAuth();
  }
};

const subscribe = (listener: () => void) => {
  ensureInitialized();
  listeners.add(listener);
  activeListenerCount += 1;

  return () => {
    listeners.delete(listener);
    activeListenerCount = Math.max(0, activeListenerCount - 1);
  };
};

const getSnapshot = () => snapshot;

const rehydrateSessionInternal = (): Promise<boolean> => {
  ensureInitialized();

  if (sessionRehydrationPromise) {
    logger.debug("[useAuth] Session rehydration already in progress, joining");
    return sessionRehydrationPromise;
  }

  sessionRehydrationPromise = (async () => {
    setAuthErrorIfDifferent(null);

    try {
      logger.debug("[useAuth] Rehydrating session from cookies");
      const freshSession = await fetchSession();
      return !!freshSession;
    } catch (error) {
      logger.error("[useAuth] Session rehydration error:", {
        error: logger.sanitizeForUser(error instanceof Error ? error.message : "Failed to rehydrate session"),
      });
      setAuthErrorIfDifferent({
        type: "network_error",
        message: logger.sanitizeForUser(error instanceof Error ? error.message : "Network error during session rehydration"),
        originalError: error,
      });
      return false;
    } finally {
      sessionRehydrationPromise = null;
    }
  })();

  return sessionRehydrationPromise;
};

const publishSessionInternal = (nextSession: Session) => {
  ensureInitialized();
  syncAuthState(nextSession);
  patchSnapshot({ isLoading: false, authError: null });
};

// The stored test-send attempts are deliberately NOT cleared here. Supabase's signOut returns an error
// and keeps the session when the request fails (a network outage), and the helpers below do not
// report that, so clearing first would discard a pending retry key for a user who is still signed
// in: a retry of the same address would then mint a new key and could deliver the test twice
// (Codex on #6606, round 6). They are cleared by the SIGNED_OUT handler in the auth listener, which
// runs only once a session has actually ended, whichever way it ended.
export interface SignOutOptions {
  /**
   * Where to land once signed out. The page is loaded there afresh (#7026), so a caller's own
   * navigate() after sign-out does not decide it. Defaults to "/auth".
   */
  redirectTo?: string;
}

// Sign-outs run one at a time, so each owns the destination while it runs: overlapping ones (the
// session timeout firing as the user clicks Sign out) would otherwise overwrite or clear each
// other's, and the SIGNED_OUT one of them emits could carry the other's (Codex on #7027).
let signOutQueue: Promise<void> = Promise.resolve();

// How long a sign-out waits for the one ahead of it. A request that stalls (a network transition)
// must not hold every later attempt, a retry once the connection is back, behind it for good
// (Codex on #7027); past this the next one goes ahead and takes the destination over.
export const SIGN_OUT_QUEUE_WAIT_MS = 10_000;

const waitAtMost = (previous: Promise<void>, ms: number) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    void previous.then(() => {
      clearTimeout(timer);
      resolve();
    });
  });

// Records the destination for the change the sign-out causes, and forgets it however the
// sign-out ends: a failed one (Supabase keeps the session and emits no SIGNED_OUT) must not leave
// it for a later, unrelated session end. Joins pendingSignOuts rather than replacing it, so a
// slower predecessor that is still genuinely in flight when this one goes ahead of it is not
// silently discarded — see pendingSignOuts above.
const withSignOutRedirect = (options: SignOutOptions | undefined, run: () => Promise<void>): Promise<void> => {
  const turn = waitAtMost(signOutQueue, SIGN_OUT_QUEUE_WAIT_MS).then(async () => {
    const mine = { redirectTo: options?.redirectTo ?? "/auth" };
    pendingSignOuts.push(mine);
    try {
      await run();
    } finally {
      const index = pendingSignOuts.indexOf(mine);
      if (index !== -1) pendingSignOuts.splice(index, 1);
    }
  });
  // A failed sign-out must not stall the ones queued behind it; its caller still sees the error.
  signOutQueue = turn.catch(() => undefined);
  return turn;
};

const logoutDeviceInternal = (options?: SignOutOptions) =>
  withSignOutRedirect(options, async () => {
    // Sign out this device only — other devices keep their sessions.
    clearAllMfaSatisfied();
    await clearSessionArtifacts();
  });

const logoutAllDevicesInternal = (options?: SignOutOptions) =>
  withSignOutRedirect(options, async () => {
    // Explicit global sign-out: revoke ALL sessions across every device.
    clearAllMfaSatisfied();
    await clearSessionArtifactsGlobal();
  });

const signOutInternal = async (options?: SignOutOptions) => {
  await logoutDeviceInternal(options);
};

export function useAuth() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return {
    user: state.user,
    session: state.session,
    sessionGeneration: state.sessionGeneration,
    isLoading: state.isLoading,
    signOut: signOutInternal,
    logoutDevice: logoutDeviceInternal,
    logoutAllDevices: logoutAllDevicesInternal,
    isAuthenticated: !!state.session,
    authError: state.authError,
    lastAuthEvent: state.lastAuthEvent,
    rehydrateSession: rehydrateSessionInternal,
    publishSession: publishSessionInternal,
    hasSessionError:
      state.authError !== null && state.authError.type !== "session_fetch_aborted",
  };
}
